import { useApi } from "../../api/hooks/use-api.ts";
import { ApiResponseType } from "../../api/types.ts";
import AuthClientStore from "../client-store/auth-client-store.ts";
import { ApiMethod } from "../../api/types.ts";
import { AccessToken, toAccessToken } from "../../../interface/AccessToken.ts";
import { withObjectPayload } from "../../api/payload.ts";

/*
 * These variables are used to debounce the refreshTokens function
 */
let debouncedPromise: Promise<unknown> | null;
let debouncedResolve: (...args: unknown[]) => void;
let debouncedReject: (...args: unknown[]) => void;
let timeout: any;

export const useAuthApi = () => {
    const { sendRequest, sendProtectedRequest } = useApi();
    
    const login = async (contactnumber: string, password: string, totpCode: string | null = null) => {
        let payload = {
            "username": contactnumber,
            "password": password,
            "grant_type": "password"
        };
        if (totpCode) {
            payload["code"] = totpCode;
        }
        const response = sendRequest(ApiMethod.POST, process.env.REACT_APP_FPSB_PORTAL_AUTH_ENDPOINT + '&token=' + process.env.REACT_APP_FPSB_PORTAL_AUTH_TOKEN, payload);

        // OK: payload is the token object. Error: { error, message } (e.g. totp_required).
        return response.then(withObjectPayload).then(data => {
            if (data.type === ApiResponseType.OK) {
                const accessToken: AccessToken | null = toAccessToken(data.payload);
                if (accessToken) {
                    AuthClientStore.setAccessToken(accessToken);
                }
            }
            return data;
        });
    };

    const checkLoginStatus = () => {
        let accessToken = AuthClientStore.getAccessToken();
        if (accessToken?.expiresAt && new Date(accessToken.expiresAt) > new Date()) {
            return true;
        }
        return false;
    }

    const logout = () => {
        AuthClientStore.removeAccessToken();
    };

    const refreshTokens = async () => {
        clearTimeout(timeout);
        if (!debouncedPromise) {
            debouncedPromise = new Promise((resolve, reject) => {
                debouncedResolve = resolve;
                debouncedReject = reject;
            });
        }

        timeout = setTimeout(() => {
            const executeLogic = async () => {
                const response = await sendProtectedRequest(
                    ApiMethod.POST,
                    process.env.REACT_APP_FPSB_PORTAL_AUTH_ENDPOINT + '&token=' + process.env.REACT_APP_FPSB_PORTAL_AUTH_TOKEN,
                    {
                        "refresh_token": AuthClientStore.getAccessToken()?.refreshToken,
                        "grant_type": "refresh_token"
                    },
                    AuthClientStore.getAccessToken()?.refreshToken,
                );

                if (response.type === ApiResponseType.OK) {
                    const accessToken: AccessToken | null = toAccessToken(response.payload);
                    if (accessToken) {
                        AuthClientStore.setAccessToken(accessToken);
                    }
                }
            };

            executeLogic().then(debouncedResolve).catch(debouncedReject);

            debouncedPromise = null;
        }, 200);

        return debouncedPromise;
    };

    const sendAuthGuardedRequest = async (
        userIsNotAuthenticatedCallback: () => void,
        method: ApiMethod,
        path: string,
        body?: any,
        init?: RequestInit,
    ) => {
        try {
            return await sendProtectedRequest(method, path, body, undefined, init);
        } catch (e) {
            if (e?.status === 401) {
                try {
                    await refreshTokens();
                } catch (e) {
                    userIsNotAuthenticatedCallback();
                    throw e;
                }
                return await sendProtectedRequest(method, path, body, undefined, init);
            }

            throw e;
        }
    };

    return { login, logout, checkLoginStatus, sendAuthGuardedRequest };
};
